import { FunctionComponent, ReactNode, useState } from 'react';
import { myPayment, AllCarRides } from '../../types/extraPayments';
import { Autocomplete, TextField, Typography, Input, Dialog, Table, TableRow, TableCell } from '../../tailwindcomponents';
import { Button } from '../../tailwindcomponents';
import ExtraPaymentsAPI from '../../api/extraPayments';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import { Edit as EditIcon } from '../../components/icons';
type PaymentDialogProps = {
    payment: myPayment,
    visible: boolean,
    onClose: Function,
    viewType: "user" | "timeControl" | "supervisor" | "all"
    displaySnackbar: Function
}

// Small label/value stack used across the modal. Renders as an uppercase muted
// caption above the value, giving each field enough breathing room to be legible.
type InfoFieldProps = {
    label: string,
    value: ReactNode,
    valueClassName?: string,
};
const InfoField: FunctionComponent<InfoFieldProps> = ({ label, value, valueClassName }) => (
    <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted">{label}</span>
        <span className={`text-sm leading-snug text-content break-words ${valueClassName ?? ''}`}>
            {value !== undefined && value !== null && value !== '' ? value : <span className="text-muted italic">—</span>}
        </span>
    </div>
);

const SectionHeader: FunctionComponent<{ title: string }> = ({ title }) => (
    <div className="col-span-full flex items-center gap-2 mt-1">
        <span className="text-xs font-semibold uppercase tracking-widest text-primary">{title}</span>
        <span className="flex-1 h-px bg-gray-200" />
    </div>
);

// Simple status pill — colour derived from payment.status keyword so the badge
// carries the same semantic weight as the surrounding text.
const statusStyles = (status: string): string => {
    const s = (status || '').toLowerCase();
    if (s.includes('declin')) return 'bg-error/10 text-error border-error/30';
    if (s.includes('confirm') || s.includes('accepted') || s.includes('approved')) return 'bg-success/10 text-success border-success/30';
    if (s.includes('supervisor')) return 'bg-info/10 text-info border-info/30';
    if (s.includes('time control')) return 'bg-secondary/20 text-content border-secondary/40';
    return 'bg-gray-100 text-content border-gray-300';
};

// Dialog for displaying payment line and approve/reject as supervisor or time control
const PaymentDialog: FunctionComponent<PaymentDialogProps> = ({ payment, viewType, visible, onClose, displaySnackbar }) => {
    const [declineReason, setDeclineReason] = useState<string>("");
    const [validationMessage, setValidationMessage] = useState<string>("");
    const [toggleSelect, setToggleSelect] = useState<boolean>(false);

    const options = ["Waiting for time control", "Declined", "Confirmed"];
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const store = useSelector((state: RootState) => state);
    const user = store.user.me;
    const isTimeControl = user.user_clearence_groups.find(x => x === "Extra Payment Admin");

    const onChangePaymentStatus = () => {
        if (isTimeControl && (payment.status === "DECLINED" || payment.status === "Confirmed")) {
            if (selectedStatus === "Waiting for time control") {
                ExtraPaymentsAPI.UpdateExtraPaymentStatus(payment.id).then(res => {
                    displaySnackbar("Payment status has successfully been changed to 'Waiting for Time Control'");
                    onClose(true);
                });
            }
            else if (selectedStatus === "Declined") {
                declinePayment();
            }
            else if (selectedStatus === "Confirmed") {
                supervisorApprove();
            }
        }
    }

    // Decline Payment. Both for supervisor and time control. Validation that reason is required
    const declinePayment = () => {
        if (declineReason.length === 0) {
            setValidationMessage("You must type a reason to decline");
        }
        else {
            setValidationMessage("")
            ExtraPaymentsAPI.DeclineSlip(payment.id, declineReason).then(res => {
                if (res.status === true) {
                    setDeclineReason("");
                    displaySnackbar("Payment has successfully been declined");
                    onClose(true);
                }
            })
        }
    }
    // Approve function for supervisor approval. Display Snackbar message triggers snackbar in parent component
    const supervisorApprove = () => {
        setValidationMessage("");
        setDeclineReason("");
        ExtraPaymentsAPI.SupervisorAccepted(payment.id).then(res => {
            if (res.status === true) {
                displaySnackbar("Payment has successfully been approved");
                onClose(true);
            }
        })
    }
    // Approve function for time control approval. Display Snackbar message triggers snackbar in parent component
    const timeControlApprove = () => {
        setValidationMessage("");
        setDeclineReason("");
        ExtraPaymentsAPI.TimeControlEmployeeAccepts(payment.id).then(res => {
            if (res.status === true) {
                displaySnackbar("Payment has successfully been approved");
                onClose(true);
            }
        })
    }
    const generateCarRideTableBody = (row: AllCarRides, index: number) => {
        return (
            <TableRow>
                <TableCell>{index}</TableCell>
                <TableCell>{row.from_location}</TableCell>
                <TableCell>{row.to_location}</TableCell>
                <TableCell>{row.trip_date_text}</TableCell>
                <TableCell>{row.reason}</TableCell>
                <TableCell>{(row.passengers !== null && row.passengers != undefined) && row.passengers.map(x => x.passengerName).join(', ')}</TableCell>
                {viewType === "timeControl" && <TableCell>{row.price} kr</TableCell>}
            </TableRow>
        )
    }
    return (
        <Dialog
            visible={visible}
            max_width={"md"} title={"Extra Payment Request"}
            context={
                        <div className="flex flex-col gap-3">
                            {/* Status banner — prominent at the top so approvers see state immediately */}
                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-background px-3 py-2">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-muted">Status</span>
                                    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-0.5 text-sm font-semibold ${statusStyles(payment.status)}`}>
                                        {payment.status}
                                    </span>
                                    {isTimeControl && (payment.status === "DECLINED" || payment.status === "Confirmed") &&
                                        <button
                                            type="button"
                                            onClick={() => setToggleSelect(!toggleSelect)}
                                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                        >
                                            <EditIcon className="h-4 w-4" />
                                            Change status
                                        </button>
                                    }
                                </div>
                                <div className="text-right">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-muted">Owner</div>
                                    <div className="text-sm font-medium text-content">{payment.employee_name}</div>
                                </div>
                            </div>

                            {/* Inline status change form (time control only) */}
                            {toggleSelect &&
                                <div className="flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-line bg-background px-3 py-2">
                                    <div className="w-[240px]">
                                        <Autocomplete
                                            fullWidth
                                            noOptionsText="No Status Selected"
                                            options={options}
                                            value={selectedStatus}
                                            clearOnEscape
                                            autoHighlight
                                            onChange={(e: any, newValue: string | null) => { setSelectedStatus(newValue === null ? "" : newValue); setDeclineReason("Declined by admin"); }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Status" variant="standard" />
                                            )} />
                                    </div>
                                    {selectedStatus &&
                                        <Button variant="contained" color="primary" onClick={() => onChangePaymentStatus()}>Change Status</Button>
                                    }
                                </div>
                            }

                            {/* Main details — responsive grid of readable label/value blocks */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                                <SectionHeader title="Timing" />
                                <InfoField label="Date" value={payment.in_date_text_short} />
                                <InfoField label="In at" value={payment.in_date_text_clock} />
                                <InfoField label="Out at" value={payment.out_date_text_clock} />
                                <InfoField label="Duration" value={payment.duration_text} />
                                <InfoField label="Created" value={payment.created_date_text} />

                                <SectionHeader title="Payment" />
                                <InfoField label="Payment types" value={payment.payment_types.toString()} />
                                <InfoField label="WO / Item No" value={`${payment.wo_number} / ${payment.item_no}`} />
                                <div className="col-span-2 md:col-span-3">
                                    <InfoField label="Description" value={payment.description} />
                                </div>

                                <SectionHeader title="Approvals" />
                                <div className="col-span-2 md:col-span-1">
                                    <InfoField
                                        label="Supervisor"
                                        value={payment.supervisor_accepted_date !== null ? `${payment.supervisor_name} — ${payment.supervisor_accepted_date_text}` : "N/A"}
                                    />
                                </div>
                                <div className="col-span-2 md:col-span-2">
                                    <InfoField
                                        label="Time control"
                                        value={payment.time_controller_employee_name !== null ? `${payment.time_controller_employee_name} — ${payment.time_controller_accepted_date_text}` : "N/A"}
                                    />
                                </div>
                            </div>

                            {/* Declined reason — highlighted card so it can't be missed */}
                            {payment.status === "DECLINED" &&
                                <div className="rounded-lg border border-error/30 bg-error/5 px-3 py-2">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-error">Declined reason</div>
                                    <div className="mt-0.5 text-sm text-content">{payment.declined_text ? payment.declined_text : "—"}</div>
                                </div>
                            }

                            {/* Car rides table stays as-is; wrap in a titled section */}
                            {payment.all_car_rides.length > 0 &&
                                <div className="rounded-lg border border-line bg-background-paper p-3">
                                    <Typography text={"Car rides"} align='left' variant='h4' fontWeight='bolder' textColor='black' margin={[0, 0, 1, 0]} />
                                    <Table
                                        headers={["From", "To", "Time", "Reason", "Passengers", ""]}
                                        rows={payment.all_car_rides}
                                        generateBody={(row: any, index: number) => generateCarRideTableBody(row, index)}
                                        defaultRowsPerPage={5}
                                    />
                                </div>
                            }

                            {/* Legacy 'Declined' (lowercase) reason line — kept for backwards compatibility */}
                            {payment.status === "Declined" &&
                                <div className="rounded-lg border border-error/30 bg-error/5 px-3 py-2">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-error">Declined</div>
                                    <div className="mt-0.5 text-sm text-content">{payment.declined_text}</div>
                                </div>
                            }

                            {/* Time control approval actions */}
                            {viewType === "timeControl" && payment.status === "Waiting for Time control" &&
                                <div className="flex flex-col gap-3 border-t border-line pt-3">
                                    <div className="flex flex-wrap justify-center gap-3">
                                        <Button variant="contained" color="primary" size="medium" onClick={() => timeControlApprove()} >Pay out extra payment ticket</Button>
                                        <Button variant="contained" color="error" size="medium" onClick={() => declinePayment()} >Decline payment</Button>
                                    </div>
                                    <div className="flex justify-center">
                                        <div className="w-full max-w-md">
                                            <Input
                                                multiline={true}
                                                label_text={"Reason to decline"}
                                                value={declineReason}
                                                help_text=""
                                                onTextChange={(value: string) => setDeclineReason(value)}
                                                type="text"
                                                width={100}
                                                textAlign="left"
                                            />
                                        </div>
                                    </div>
                                    {validationMessage.length > 0 &&
                                        <div className="flex justify-center">
                                            <Typography variant={'body1'} color="error" fontWeight="bold" align={'center'} text={validationMessage} />
                                        </div>
                                    }
                                </div>
                            }

                            {/* Supervisor approval actions */}
                            {viewType === "supervisor" && payment.status === "Waiting for supervisor" &&
                                <div className="flex flex-col gap-3 border-t border-line pt-3">
                                    <div className="flex flex-wrap justify-center gap-3">
                                        <Button variant="contained" color="primary" size="medium" onClick={() => supervisorApprove()} >Confirm extra payment</Button>
                                        <Button variant="contained" color="error" size="medium" onClick={() => declinePayment()} >Decline payment</Button>
                                    </div>
                                    <div className="flex justify-center">
                                        <div className="w-full max-w-md">
                                            <Input
                                                multiline={true}
                                                label_text={"Reason to decline"}
                                                value={declineReason}
                                                help_text=""
                                                onTextChange={(value: string) => setDeclineReason(value)}
                                                type="text"
                                                width={100}
                                                textAlign="left"
                                            />
                                        </div>
                                    </div>
                                    {validationMessage.length > 0 &&
                                        <div className="flex justify-center">
                                            <Typography variant={'body1'} color="error" fontWeight="bold" align={'center'} text={validationMessage} />
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                    onClose={(status: any) => { onClose(false) }}
                    strict={false}
                    show_options={false}
                />
    );
};
export default PaymentDialog;